"use client";
import { useNoteContext } from "@/contexts/NoteContext";
import { getIcon, loadIcon } from "@iconify/react";
import { iconColorCss, parseIconValue } from "@/components/tailwind/editor/renderIcon";
import { useEffect } from "react";

type IconData = NonNullable<ReturnType<typeof getIcon>>;

const DEFAULT_FAVICON = "/favicon.ico";

function setFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

function emojiToFaviconUrl(emoji: string): string {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext("2d");
  if (!ctx) return DEFAULT_FAVICON;
  ctx.font = "52px serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(emoji, 32, 36);
  return canvas.toDataURL("image/png");
}

function sanitizeSvgBody(body: string): string {
  // Strip dangerous elements that could enable XSS via SVG injection
  return body
    .replace(/<script[\s>][\s\S]*?<\/script>/gi, "")
    .replace(/<foreignObject[\s>][\s\S]*?<\/foreignObject>/gi, "")
    .replace(/\bon\w+\s*=/gi, "data-removed=");
}

/** Colours come from our own token list, but they land in an SVG attribute —
 *  drop anything that could close it. */
function sanitizeColor(value: string): string | undefined {
  return /^[#a-z0-9(),.%\s-]+$/i.test(value) ? value : undefined;
}

/**
 * Resolves an icon value's colour id to a literal colour.
 *
 * `iconColorCss` hands back a CSS token ("var(--novel-text-pink)"), which only
 * means something inside the page. A favicon is its own document and can't see
 * the app's custom properties, so read the computed value off <html> — that
 * also picks up whichever of the light/dark blocks is currently active.
 */
function resolveIconColor(color: string | null): string | undefined {
  const cssValue = iconColorCss(color);
  if (!cssValue) return undefined;
  const varName = cssValue.match(/^var\((--[\w-]+)\)$/)?.[1];
  if (!varName) return sanitizeColor(cssValue);
  const resolved = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return resolved ? sanitizeColor(resolved) : undefined;
}

function iconDataToFaviconUrl(iconData: IconData, color?: string): string {
  const { body, width = 24, height = 24 } = iconData;
  const sanitizedBody = sanitizeSvgBody(body);
  // Iconify bodies paint with `currentColor`, which resolves against the `color`
  // property — so setting it on the root <svg> tints the glyph.
  const style = color ? ` style="color:${color}"` : "";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"${style}>${sanitizedBody}</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export default function DynamicTitle() {
  const { activeTitle, editorTitle, documentTitle, documentIcon } = useNoteContext();

  const title = documentTitle || activeTitle || editorTitle || "Closot";

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    let cancelled = false;
    const apply = (href: string) => {
      if (!cancelled) setFavicon(href);
    };

    if (!documentIcon) {
      apply(DEFAULT_FAVICON);
    } else if (documentIcon.startsWith("/")) {
      // Image URL — only allow same-origin paths, reject external URLs (tracking pixel risk)
      apply(documentIcon);
    } else if (documentIcon.startsWith("http://") || documentIcon.startsWith("https://")) {
      // Block external favicon URLs to prevent tracking pixels
      apply(DEFAULT_FAVICON);
    } else if (documentIcon.includes(":")) {
      // Iconify string, optionally colour-suffixed ("tabler:home|pink"). Parse
      // the suffix off first — getIcon() doesn't know that spelling and would
      // fall through to the default favicon.
      const { icon, color } = parseIconValue(documentIcon);
      const cssColor = resolveIconColor(color);
      const cached = getIcon(icon);
      if (cached) {
        apply(iconDataToFaviconUrl(cached, cssColor));
      } else {
        // Not in Iconify's cache yet (cold load, before the page renders the
        // same glyph) — fetch it, then stamp the favicon.
        apply(DEFAULT_FAVICON);
        loadIcon(icon)
          .then((data) => apply(iconDataToFaviconUrl(data, cssColor)))
          .catch(() => {
            /* unknown icon name — keep the default favicon */
          });
      }
    } else {
      apply(emojiToFaviconUrl(documentIcon));
    }

    return () => {
      cancelled = true;
    };
  }, [documentIcon]);

  return null;
}
